import React, { useState, useEffect, useCallback } from 'react';
import { getPurchaseHistory, getInventories, getVendors, getItems, getCategories, getBrands, exportPurchases, getInvoiceDetails, editInvoice, getInvoiceAuditTrail, getSettings, deleteInvoice, lookupVendorPricing, getItemVendorPricing } from '../lib/api';
import { toast } from 'sonner';
import { downloadBlob } from '../lib/downloadHelper';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../components/ui/command';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import {
  Search, Loader2, DollarSign, FileText, ShoppingCart, Eye, ChevronUp, ChevronDown,
  Download, Printer, Pencil, Plus, Trash2, History, AlertCircle, CheckCircle, AlertTriangle, Lock, Check
} from 'lucide-react';
import { Alert, AlertDescription } from '../components/ui/alert';
import { ItemThumbnail } from '../components/ImageUploader';

const PurchaseHistory = () => {
  const { isManager, isAdmin } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [inventories, setInventories] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [allItems, setAllItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedInventory, setSelectedInventory] = useState('all');
  const [selectedVendor, setSelectedVendor] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [exporting, setExporting] = useState(false);

  // Edit state
  const [editMode, setEditMode] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');
  const [editSuccess, setEditSuccess] = useState('');
  const [editVendorId, setEditVendorId] = useState('');
  const [editLineItems, setEditLineItems] = useState([]);
  const [newEditItem, setNewEditItem] = useState({ item_id: '', quantity: '', unit_price: '' });

  // Edit item filter state
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [editFilterCategory, setEditFilterCategory] = useState('all');
  const [editFilterSubCategory, setEditFilterSubCategory] = useState('all');
  const [editFilterBrand, setEditFilterBrand] = useState('all');
  const [editItemSearch, setEditItemSearch] = useState('');
  const [showEditItemDropdown, setShowEditItemDropdown] = useState(false);

  // Edit item package state
  const [editItemPackages, setEditItemPackages] = useState([]);
  const [editSelectedPackage, setEditSelectedPackage] = useState(null);
  const [editPackagesQty, setEditPackagesQty] = useState('');
  const [editPricePerPackage, setEditPricePerPackage] = useState('');

  // Audit trail state
  const [auditOpen, setAuditOpen] = useState(false);
  const [auditData, setAuditData] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [taxRate, setTaxRate] = useState(0);

  // Delete state
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [deleting, setDeleting] = useState(false);

  // Price lookup loading
  const [priceLookupLoading, setPriceLookupLoading] = useState(false);

  useEffect(() => {
    Promise.all([getInventories(), getVendors(), getItems(), getSettings(), getCategories(), getBrands()]).then(([invRes, venRes, itemsRes, settingsRes, catRes, brandRes]) => {
      setInventories(invRes.data);
      setVendors(venRes.data.filter(v => v.active !== false));
      setAllItems(itemsRes.data);
      setTaxRate(settingsRes.data.sales_tax_rate || 0);
      setCategories(catRes.data);
      setBrands(brandRes.data);
    }).catch(() => {});
  }, []);

  const fetchPurchases = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (searchQuery) params.invoice_number = searchQuery;
      if (selectedInventory !== 'all') params.inventory_id = selectedInventory;
      if (selectedVendor !== 'all') params.vendor_id = selectedVendor;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      const res = await getPurchaseHistory(params);
      setInvoices(res.data);
    } catch (err) {
      toast.error('Failed to load purchase history');
    } finally {
      setLoading(false);
    }
  }, [searchQuery, selectedInventory, selectedVendor, startDate, endDate]);

  useEffect(() => { fetchPurchases(); }, [selectedInventory, selectedVendor, startDate, endDate]);
  useEffect(() => { const t = setTimeout(() => fetchPurchases(), 300); return () => clearTimeout(t); }, [searchQuery]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = {};
      if (searchQuery) params.invoice_number = searchQuery;
      if (selectedInventory !== 'all') params.inventory_id = selectedInventory;
      if (selectedVendor !== 'all') params.vendor_id = selectedVendor;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      const res = await exportPurchases(params);
      downloadBlob(res.data, `purchase_history_${new Date().toISOString().split('T')[0]}.xlsx`);
    } catch (err) {
      alert('Export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const handlePrint = () => { window.print(); };

  // === EDIT LOGIC ===
  const openEditMode = async (invoice) => {
    setEditLoading(true);
    setEditError('');
    setEditSuccess('');
    try {
      const res = await getInvoiceDetails(invoice.invoice_number);
      const data = res.data;
      setEditVendorId(data.vendor_id);
      setEditLineItems(data.items.map(i => ({
        item_id: i.item_id,
        item_name: i.item_name,
        internal_code: i.internal_code,
        quantity: i.quantity,
        unit_price: i.unit_price,
        is_original: true
      })));
      setEditMode(true);
    } catch (err) {
      setEditError(err.response?.data?.detail || 'Failed to load invoice for editing');
    } finally {
      setEditLoading(false);
    }
  };

  const addEditLineItem = () => {
    const hasPackage = !!editSelectedPackage;

    if (hasPackage && editPackagesQty) {
      if (!newEditItem.item_id) return;
      const pkgQty = parseInt(editPackagesQty);
      const pkgPrice = parseFloat(editPricePerPackage);
      if (pkgQty <= 0 || !pkgPrice || pkgPrice <= 0) { setEditError('Enter valid packages qty and price'); return; }
      const totalUnits = pkgQty * (editSelectedPackage.units_per_package || 1);
      const unitCost = Math.round((pkgPrice / (editSelectedPackage.units_per_package || 1)) * 10000) / 10000;
      const item = allItems.find(i => i.id === newEditItem.item_id);
      setEditLineItems(prev => [...prev, {
        item_id: newEditItem.item_id,
        item_name: item?.item_name || 'Unknown',
        internal_code: item?.internal_code || '',
        quantity: totalUnits,
        unit_price: unitCost,
        is_original: false,
        package_label: editSelectedPackage.package_label,
        packages_qty: pkgQty,
        units_per_package: editSelectedPackage.units_per_package || 1
      }]);
    } else {
      if (!newEditItem.item_id || !newEditItem.quantity || !newEditItem.unit_price) return;
      const item = allItems.find(i => i.id === newEditItem.item_id);
      setEditLineItems(prev => [...prev, {
        item_id: newEditItem.item_id,
        item_name: item?.item_name || 'Unknown',
        internal_code: item?.internal_code || '',
        quantity: parseInt(newEditItem.quantity),
        unit_price: parseFloat(newEditItem.unit_price),
        is_original: false
      }]);
    }
    setNewEditItem({ item_id: '', quantity: '', unit_price: '' });
    setEditItemSearch('');
    setEditItemPackages([]);
    setEditSelectedPackage(null);
    setEditPackagesQty('');
    setEditPricePerPackage('');
    setEditError('');
  };

  const removeEditLineItem = (index) => {
    setEditLineItems(prev => prev.filter((_, i) => i !== index));
  };

  const updateEditLineItem = (index, field, value) => {
    setEditLineItems(prev => prev.map((li, i) =>
      i === index ? { ...li, [field]: field === 'quantity' ? parseInt(value) || 0 : parseFloat(value) || 0 } : li
    ));
  };

  const handleSaveEdit = async () => {
    if (editLineItems.length === 0) {
      setEditError('Invoice must have at least one item');
      return;
    }
    setEditSaving(true);
    setEditError('');
    setEditSuccess('');
    try {
      const res = await editInvoice(selectedInvoice.invoice_number, {
        vendor_id: editVendorId,
        line_items: editLineItems.map(li => ({
          item_id: li.item_id,
          quantity: li.quantity,
          unit_price: li.unit_price
        }))
      });
      setEditSuccess(`Invoice updated. ${res.data.changes} change(s) recorded.`);
      setEditMode(false);
      fetchPurchases();
      // Refresh the selected invoice view
      const updatedInvoice = { ...selectedInvoice };
      const vendor = vendors.find(v => v.id === editVendorId);
      updatedInvoice.vendor_name = vendor?.vendor_name || updatedInvoice.vendor_name;
      updatedInvoice.items = editLineItems.map(li => ({
        item_name: li.item_name,
        internal_code: li.internal_code,
        quantity: li.quantity,
        unit_price: li.unit_price,
        total_cost: li.quantity * li.unit_price,
        inventory_name: selectedInvoice.inventory_name
      }));
      updatedInvoice.total_cost = editLineItems.reduce((s, li) => s + li.quantity * li.unit_price, 0);
      updatedInvoice.item_count = editLineItems.length;
      setSelectedInvoice(updatedInvoice);
    } catch (err) {
      setEditError(err.response?.data?.detail || 'Failed to save changes');
    } finally {
      setEditSaving(false);
    }
  };

  const cancelEdit = () => {
    setEditMode(false);
    setEditError('');
    setEditSuccess('');
  };

  // === AUDIT TRAIL ===
  const openAuditTrail = async (invoice) => {
    setAuditLoading(true);
    setAuditOpen(true);
    try {
      const res = await getInvoiceAuditTrail(invoice.invoice_number);
      setAuditData(res.data);
    } catch {
      setAuditData([]);
    } finally {
      setAuditLoading(false);
    }
  };

  // === DELETE INVOICE ===
  const openDeleteDialog = (invoice) => {
    setDeleteTarget(invoice);
    setDeletePassword('');
    setDeleteError('');
    setDeleteOpen(true);
  };

  const handleDelete = async () => {
    if (!deletePassword) { setDeleteError('Password is required'); return; }
    setDeleting(true);
    setDeleteError('');
    try {
      const res = await deleteInvoice(deleteTarget.invoice_number, deletePassword);
      setInvoices(prev => prev.filter(inv => inv.invoice_number !== deleteTarget.invoice_number));
      toast.success(`Invoice ${deleteTarget.invoice_number} deleted. ${res.data.deleted_transactions} transaction(s) reversed.`);
      setDeleteOpen(false);
      setDetailOpen(false);
      setSelectedInvoice(null);
      setDeleteTarget(null);
      setSearchQuery('');
    } catch (err) {
      setDeleteError(err.response?.data?.detail || 'Failed to delete invoice');
    } finally {
      setDeleting(false);
    }
  };

  // === EDIT: Auto-fill price from vendor pricing ===
  const handleEditItemSelect = async (itemId) => {
    setNewEditItem({ ...newEditItem, item_id: itemId, unit_price: '' });
    setEditItemPackages([]);
    setEditSelectedPackage(null);
    setEditPackagesQty('');
    setEditPricePerPackage('');
    if (!itemId || !editVendorId) return;
    setPriceLookupLoading(true);
    try {
      const pricingRes = await getItemVendorPricing(itemId);
      const vendorPricings = pricingRes.data.filter(p => p.vendor_id === editVendorId);
      if (vendorPricings.length > 0) {
        setEditItemPackages(vendorPricings);
        const defaultPkg = vendorPricings.find(p => (p.units_per_package || 1) === 1) || vendorPricings[0];
        setEditSelectedPackage(defaultPkg);
        const pkgPrice = defaultPkg.package_price || defaultPkg.unit_price * (defaultPkg.units_per_package || 1);
        setEditPricePerPackage(String(pkgPrice));
        setNewEditItem(prev => ({ ...prev, item_id: itemId, unit_price: String(defaultPkg.unit_price) }));
      } else {
        const res = await lookupVendorPricing(itemId, editVendorId);
        if (res.data.found) {
          setNewEditItem(prev => ({ ...prev, item_id: itemId, unit_price: res.data.unit_price.toString() }));
        }
      }
    } catch {
      setNewEditItem(prev => ({ ...prev, item_id: itemId, unit_price: '' }));
    } finally {
      setPriceLookupLoading(false);
    }
  };

  const sortedInvoices = [...invoices].sort((a, b) => {
    if (sortBy === 'invoice_number') return sortOrder === 'asc' ? a.invoice_number.localeCompare(b.invoice_number) : b.invoice_number.localeCompare(a.invoice_number);
    if (sortBy === 'vendor_name') return sortOrder === 'asc' ? a.vendor_name.localeCompare(b.vendor_name) : b.vendor_name.localeCompare(a.vendor_name);
    if (sortBy === 'total_cost') return sortOrder === 'asc' ? a.total_cost - b.total_cost : b.total_cost - a.total_cost;
    return sortOrder === 'asc' ? a.date.localeCompare(b.date) : b.date.localeCompare(a.date);
  });

  const totalSpend = invoices.reduce((sum, i) => sum + i.total_cost, 0);
  const handleSort = (col) => { if (sortBy === col) setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc'); else { setSortBy(col); setSortOrder('desc'); } };
  const SortIcon = ({ column }) => { if (sortBy !== column) return null; return sortOrder === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />; };

  const editTotalValue = editLineItems.reduce((sum, li) => sum + (li.quantity * li.unit_price), 0);

  // Edit item filter helpers
  const parentCategories = categories.filter(c => !c.parent_id);
  const getSubcategories = (parentId) => categories.filter(c => c.parent_id === parentId);
  const editFilterSubcategories = editFilterCategory !== 'all' ? getSubcategories(editFilterCategory) : categories.filter(c => c.parent_id);
  const getCategoryName = (catId) => {
    const cat = categories.find(c => c.id === catId);
    if (!cat) return '';
    const parent = cat.parent_id ? categories.find(c => c.id === cat.parent_id) : null;
    return parent ? `${parent.category_name} > ${cat.category_name}` : cat.category_name;
  };
  const getBrandName = (brandId) => brands.find(b => b.id === brandId)?.brand_name || '';

  const filteredEditItems = allItems.filter(item => {
    if (editFilterCategory !== 'all') {
      const childIds = getSubcategories(editFilterCategory).map(c => c.id);
      if (!childIds.includes(item.category_id)) return false;
    }
    if (editFilterSubCategory !== 'all' && item.category_id !== editFilterSubCategory) return false;
    if (editFilterBrand !== 'all' && item.brand_id !== editFilterBrand) return false;
    if (editItemSearch.trim()) {
      const q = editItemSearch.toLowerCase();
      return item.item_name.toLowerCase().includes(q) || (item.internal_code || '').toLowerCase().includes(q) || (item.vendor_skus || []).some(sku => sku.toLowerCase().includes(q));
    }
    return true;
  }).slice(0, 20);

  return (
    <div className="space-y-6 animate-slide-in" data-testid="purchase-history-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="heading-2 text-[#1F2937]">Purchase History</h1>
          <p className="text-[#6B7280]">Search and track purchases by invoice, vendor, and item</p>
        </div>
        <div className="flex gap-2 print:hidden">
          <Button onClick={handleExport} disabled={exporting || invoices.length === 0} className="bg-green-600 hover:bg-green-700 text-white rounded-lg gap-2" data-testid="export-purchases-btn">
            {exporting ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />} Export Excel
          </Button>
          <Button onClick={handlePrint} variant="outline" className="rounded-lg gap-2 border-[#0776BD] text-[#0776BD]" data-testid="print-purchases-btn">
            <Printer size={16} /> Print
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className={`grid grid-cols-1 gap-4 print:hidden ${taxRate > 0 ? 'md:grid-cols-4' : 'md:grid-cols-3'}`}>
        <Card className="bg-white rounded-xl border border-gray-200 shadow-soft">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-[#E6F2FA] rounded-xl"><FileText size={24} className="text-[#0776BD]" /></div>
              <div>
                <p className="text-sm font-medium text-[#6B7280] uppercase tracking-wide">Invoices</p>
                <p className="text-3xl font-bold font-heading text-[#1F2937]" data-testid="invoice-count">{invoices.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white rounded-xl border border-gray-200 shadow-soft">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-[#FFF8E1] rounded-xl"><DollarSign size={24} className="text-[#F0C500]" /></div>
              <div>
                <p className="text-sm font-medium text-[#6B7280] uppercase tracking-wide">{taxRate > 0 ? 'Subtotal' : 'Total Spend'}</p>
                <p className="text-3xl font-bold font-heading text-[#1F2937]" data-testid="purchase-total-spend">${totalSpend.toFixed(2)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        {taxRate > 0 && (
          <Card className="bg-white rounded-xl border border-gray-200 shadow-soft">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-orange-100 rounded-xl"><DollarSign size={24} className="text-orange-600" /></div>
                <div>
                  <p className="text-sm font-medium text-[#6B7280] uppercase tracking-wide">Total After Tax ({taxRate}%)</p>
                  <p className="text-3xl font-bold font-heading text-[#1F2937]">${(totalSpend * (1 + taxRate / 100)).toFixed(2)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
        <Card className="bg-white rounded-xl border border-gray-200 shadow-soft">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-100 rounded-xl"><ShoppingCart size={24} className="text-green-600" /></div>
              <div>
                <p className="text-sm font-medium text-[#6B7280] uppercase tracking-wide">Total Items</p>
                <p className="text-3xl font-bold font-heading text-[#1F2937]">{invoices.reduce((sum, i) => sum + i.item_count, 0)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="bg-white rounded-xl border border-gray-200 shadow-soft print:hidden">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]" size={20} />
              <Input placeholder="Search by invoice number..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} autoComplete="off" className="pl-10 h-11 rounded-lg border-gray-300" data-testid="search-invoices-input" />
            </div>
            <Select value={selectedVendor} onValueChange={setSelectedVendor}>
              <SelectTrigger className="w-full md:w-[200px] h-11 rounded-lg border-gray-300" data-testid="purchase-vendor-filter"><SelectValue placeholder="All Vendors" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Vendors</SelectItem>
                {vendors.map(v => <SelectItem key={v.id} value={v.id}>{v.vendor_name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={selectedInventory} onValueChange={setSelectedInventory}>
              <SelectTrigger className="w-full md:w-[200px] h-11 rounded-lg border-gray-300" data-testid="purchase-inventory-filter"><SelectValue placeholder="All Inventories" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Inventories</SelectItem>
                {inventories.map(inv => <SelectItem key={inv.id} value={inv.id}>{inv.inventory_name}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="flex gap-2">
              <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="h-11 rounded-lg border-gray-300 text-sm" data-testid="purchase-start-date" />
              <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="h-11 rounded-lg border-gray-300 text-sm" data-testid="purchase-end-date" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Print header */}
      <div className="hidden print:block mb-4">
        <h1 className="text-xl font-bold">Mr. Electric of Southwest Las Vegas</h1>
        <h2 className="text-lg">Purchase History Report</h2>
        <p className="text-sm text-gray-600">{new Date().toLocaleString()}</p>
      </div>

      {/* Invoices Table */}
      <Card className="bg-white rounded-xl border border-gray-200 shadow-soft print:shadow-none print:border-0">
        <CardHeader className="print:hidden">
          <CardTitle className="font-heading text-lg text-[#1F2937] flex items-center gap-2">
            <FileText size={20} className="text-[#0776BD]" /> Invoices ({sortedInvoices.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-[#0776BD]" /></div>
          ) : sortedInvoices.length === 0 ? (
            <div className="py-12 text-center text-[#6B7280]">
              <FileText size={48} className="mx-auto mb-4 opacity-50" />
              <p>No purchase records found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#F5F6F7]">
                    <TableHead className="font-semibold text-[#1F2937] cursor-pointer hover:bg-gray-200" onClick={() => handleSort('invoice_number')}>
                      <div className="flex items-center gap-1">Invoice # <SortIcon column="invoice_number" /></div>
                    </TableHead>
                    <TableHead className="font-semibold text-[#1F2937] cursor-pointer hover:bg-gray-200" onClick={() => handleSort('vendor_name')}>
                      <div className="flex items-center gap-1">Vendor <SortIcon column="vendor_name" /></div>
                    </TableHead>
                    <TableHead className="font-semibold text-[#1F2937] cursor-pointer hover:bg-gray-200" onClick={() => handleSort('date')}>
                      <div className="flex items-center gap-1">Date <SortIcon column="date" /></div>
                    </TableHead>
                    <TableHead className="font-semibold text-[#1F2937] text-center">Items</TableHead>
                    <TableHead className="font-semibold text-[#1F2937] text-right cursor-pointer hover:bg-gray-200" onClick={() => handleSort('total_cost')}>
                      <div className="flex items-center justify-end gap-1">Cost <SortIcon column="total_cost" /></div>
                    </TableHead>
                    {taxRate > 0 && (
                      <TableHead className="font-semibold text-[#1F2937] text-right">Tax ({taxRate}%)</TableHead>
                    )}
                    {taxRate > 0 && (
                      <TableHead className="font-semibold text-[#1F2937] text-right">Total w/ Tax</TableHead>
                    )}
                    <TableHead className="font-semibold text-[#1F2937] text-right print:hidden">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedInvoices.map((inv) => (
                    <TableRow key={inv.invoice_number} className="hover:bg-[#F5F6F7]" data-testid={`invoice-row-${inv.invoice_number}`}>
                      <TableCell><span className="font-medium text-[#0776BD]">{inv.invoice_number}</span></TableCell>
                      <TableCell className="text-[#1F2937]">{inv.vendor_name}</TableCell>
                      <TableCell className="text-[#6B7280]">{new Date(inv.date).toLocaleDateString()}</TableCell>
                      <TableCell className="text-center"><Badge className="bg-[#E6F2FA] text-[#0776BD] border-0">{inv.item_count}</Badge></TableCell>
                      <TableCell className="text-right font-bold text-[#1F2937] text-lg">${inv.total_cost.toFixed(2)}</TableCell>
                      {taxRate > 0 && (
                        <TableCell className="text-right text-[#6B7280]">${(inv.total_cost * taxRate / 100).toFixed(2)}</TableCell>
                      )}
                      {taxRate > 0 && (
                        <TableCell className="text-right font-bold text-[#0776BD] text-lg">${(inv.total_cost * (1 + taxRate / 100)).toFixed(2)}</TableCell>
                      )}
                      <TableCell className="text-right print:hidden">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => { setSelectedInvoice(inv); setEditMode(false); setEditSuccess(''); setEditError(''); setDetailOpen(true); }} className="hover:bg-[#E6F2FA] text-[#0776BD] gap-1" data-testid={`view-invoice-${inv.invoice_number}`}>
                            <Eye size={16} /> View
                          </Button>
                          {isManager() && (
                            <Button variant="ghost" size="sm" onClick={() => openAuditTrail(inv)} className="hover:bg-amber-50 text-amber-600 gap-1" data-testid={`audit-invoice-${inv.invoice_number}`}>
                              <History size={16} />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Invoice Detail / Edit Dialog */}
      <Dialog open={detailOpen} onOpenChange={(open) => { if (!open) { setDetailOpen(false); setEditMode(false); } }}>
        <DialogContent className="max-w-3xl rounded-xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-[#1F2937] flex items-center gap-2">
              <FileText className="text-[#0776BD]" />
              Invoice: {selectedInvoice?.invoice_number}
              {editMode && <Badge className="bg-amber-100 text-amber-700 border-0 ml-2">Editing</Badge>}
            </DialogTitle>
          </DialogHeader>

          {editError && (
            <Alert variant="destructive" className="rounded-lg">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{editError}</AlertDescription>
            </Alert>
          )}
          {editSuccess && (
            <Alert className="rounded-lg border-green-200 bg-green-50">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-700">{editSuccess}</AlertDescription>
            </Alert>
          )}

          {selectedInvoice && !editMode && (
            <div className="space-y-4">
              <div className={`grid gap-4 ${taxRate > 0 ? 'grid-cols-4' : 'grid-cols-3'}`}>
                <div className="p-4 bg-[#E6F2FA] rounded-lg text-center">
                  <p className="text-sm text-[#6B7280]">Subtotal</p>
                  <p className="text-2xl font-bold text-[#0776BD]">${selectedInvoice.total_cost.toFixed(2)}</p>
                </div>
                {taxRate > 0 && (
                  <div className="p-4 bg-orange-50 rounded-lg text-center">
                    <p className="text-sm text-[#6B7280]">Tax ({taxRate}%)</p>
                    <p className="text-2xl font-bold text-orange-600">${(selectedInvoice.total_cost * taxRate / 100).toFixed(2)}</p>
                  </div>
                )}
                <div className="p-4 bg-[#F5F6F7] rounded-lg text-center">
                  <p className="text-sm text-[#6B7280]">Vendor</p>
                  <p className="text-sm font-medium text-[#1F2937]">{selectedInvoice.vendor_name}</p>
                </div>
                <div className="p-4 bg-[#F5F6F7] rounded-lg text-center">
                  <p className="text-sm text-[#6B7280]">{taxRate > 0 ? 'Total After Tax' : 'Date'}</p>
                  <p className={`font-bold text-[#1F2937] ${taxRate > 0 ? 'text-2xl' : 'text-sm'}`}>
                    {taxRate > 0
                      ? `$${(selectedInvoice.total_cost * (1 + taxRate / 100)).toFixed(2)}`
                      : new Date(selectedInvoice.date).toLocaleDateString()}
                  </p>
                </div>
              </div>
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#F5F6F7]">
                    <TableHead className="font-semibold text-[#1F2937]">Item</TableHead>
                    <TableHead className="font-semibold text-[#1F2937]">Inventory</TableHead>
                    <TableHead className="font-semibold text-[#1F2937] text-center">Qty</TableHead>
                    <TableHead className="font-semibold text-[#1F2937] text-right">Unit Price</TableHead>
                    <TableHead className="font-semibold text-[#1F2937] text-right">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {selectedInvoice.items.map((item, idx) => (
                    <TableRow key={`${item.item_name}-${idx}`}>
                      <TableCell>
                        <div className="font-medium text-[#1F2937]">{item.item_name}</div>
                        <div className="text-xs text-[#0776BD] font-mono">({item.internal_code})</div>
                      </TableCell>
                      <TableCell className="text-[#6B7280]">{item.inventory_name}</TableCell>
                      <TableCell className="text-center">{item.quantity}</TableCell>
                      <TableCell className="text-right text-[#6B7280]">${item.unit_price.toFixed(2)}</TableCell>
                      <TableCell className="text-right font-medium">${item.total_cost.toFixed(2)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="border-t-2 border-[#0776BD]">
                    <TableCell colSpan={4} className="text-right font-bold text-[#1F2937]">Subtotal</TableCell>
                    <TableCell className="text-right font-bold text-xl text-[#0776BD]">${selectedInvoice.total_cost.toFixed(2)}</TableCell>
                  </TableRow>
                  {taxRate > 0 && (
                    <>
                      <TableRow>
                        <TableCell colSpan={4} className="text-right font-medium text-[#6B7280]">Tax ({taxRate}%)</TableCell>
                        <TableCell className="text-right font-medium text-[#6B7280]">${(selectedInvoice.total_cost * taxRate / 100).toFixed(2)}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={4} className="text-right font-bold text-[#1F2937]">Total After Tax</TableCell>
                        <TableCell className="text-right font-bold text-xl text-[#0776BD]">${(selectedInvoice.total_cost * (1 + taxRate / 100)).toFixed(2)}</TableCell>
                      </TableRow>
                    </>
                  )}
                </TableBody>
              </Table>
            </div>
          )}

          {/* EDIT MODE */}
          {selectedInvoice && editMode && (
            <div className="space-y-4">
              {/* Vendor selection */}
              <div className="space-y-2">
                <Label className="text-sm font-medium text-[#1F2937]">Vendor</Label>
                <Select value={editVendorId} onValueChange={setEditVendorId}>
                  <SelectTrigger className="h-11 rounded-lg border-gray-300" data-testid="edit-vendor-select">
                    <SelectValue placeholder="Select vendor" />
                  </SelectTrigger>
                  <SelectContent>
                    {vendors.map(v => <SelectItem key={v.id} value={v.id}>{v.vendor_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              {/* Existing line items - editable */}
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#F5F6F7]">
                    <TableHead className="font-semibold text-[#1F2937]">Item</TableHead>
                    <TableHead className="font-semibold text-[#1F2937] text-center w-28">Qty</TableHead>
                    <TableHead className="font-semibold text-[#1F2937] text-right w-32">Unit Price</TableHead>
                    <TableHead className="font-semibold text-[#1F2937] text-right w-28">Total</TableHead>
                    <TableHead className="w-10"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {editLineItems.map((li, idx) => (
                    <TableRow key={`${li.item_id}-${li.package_label || idx}`} data-testid={`edit-line-${idx}`}>
                      <TableCell>
                        <div className="font-medium text-[#1F2937]">{li.item_name}</div>
                        <div className="text-xs text-[#0776BD] font-mono">({li.internal_code})</div>
                        {li.package_label && (
                          <div className="text-xs text-blue-600 mt-0.5">{li.packages_qty} x {li.package_label} ({li.units_per_package} unit{li.units_per_package > 1 ? 's' : ''} each)</div>
                        )}
                        {!li.is_original && <Badge className="bg-green-100 text-green-700 border-0 text-xs mt-1">New</Badge>}
                      </TableCell>
                      <TableCell className="text-center">
                        <Input type="number" min="1" value={li.quantity} onChange={(e) => updateEditLineItem(idx, 'quantity', e.target.value)} className="h-9 text-center rounded-lg border-gray-300 w-20 mx-auto" data-testid={`edit-qty-${idx}`} />
                      </TableCell>
                      <TableCell className="text-right">
                        <Input type="number" min="0" step="0.01" value={li.unit_price} onChange={(e) => updateEditLineItem(idx, 'unit_price', e.target.value)} className="h-9 text-right rounded-lg border-gray-300 w-28 ml-auto" data-testid={`edit-price-${idx}`} />
                      </TableCell>
                      <TableCell className="text-right font-medium text-[#0776BD]">${(li.quantity * li.unit_price).toFixed(2)}</TableCell>
                      <TableCell>
                        <Button type="button" variant="ghost" size="icon" onClick={() => removeEditLineItem(idx)} className="hover:bg-red-50 text-red-500 h-8 w-8" data-testid={`edit-remove-${idx}`}>
                          <Trash2 size={16} />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="border-t-2 border-[#0776BD]">
                    <TableCell colSpan={3} className="text-right font-bold text-[#1F2937]">Subtotal</TableCell>
                    <TableCell className="text-right font-bold text-xl text-[#0776BD]">${editTotalValue.toFixed(2)}</TableCell>
                    <TableCell />
                  </TableRow>
                  {taxRate > 0 && (
                    <>
                      <TableRow>
                        <TableCell colSpan={3} className="text-right font-medium text-[#6B7280]">Tax ({taxRate}%)</TableCell>
                        <TableCell className="text-right font-medium text-[#6B7280]">${(editTotalValue * taxRate / 100).toFixed(2)}</TableCell>
                        <TableCell />
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={3} className="text-right font-bold text-[#1F2937]">Total After Tax</TableCell>
                        <TableCell className="text-right font-bold text-xl text-[#0776BD]">${(editTotalValue * (1 + taxRate / 100)).toFixed(2)}</TableCell>
                        <TableCell />
                      </TableRow>
                    </>
                  )}
                </TableBody>
              </Table>

              {/* Add new item */}
              <div className="p-4 bg-[#F5F6F7] rounded-lg border-2 border-dashed border-gray-300 space-y-3">
                <p className="text-sm font-medium text-[#6B7280] mb-1">Add Item to Invoice</p>

                {/* Filter row */}
                <div className="flex flex-wrap gap-2">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="h-8 justify-between rounded-lg border-gray-300 font-normal text-xs min-w-[120px]" data-testid="edit-filter-category">
                        <span className="truncate">{editFilterCategory !== 'all' ? parentCategories.find(c => c.id === editFilterCategory)?.category_name || 'Category' : 'Category'}</span>
                        <ChevronDown className="ml-1 h-3 w-3 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[200px] p-0" align="start">
                      <Command>
                        <CommandInput placeholder="Search..." />
                        <CommandList className="max-h-[200px]">
                          <CommandEmpty>No results found</CommandEmpty>
                          <CommandGroup>
                            <CommandItem onSelect={() => setEditFilterCategory('all')}>
                              <Check className={`mr-2 h-3.5 w-3.5 ${editFilterCategory === 'all' ? 'opacity-100' : 'opacity-0'}`} />
                              All Categories
                            </CommandItem>
                            {parentCategories.map(cat => (
                              <CommandItem key={cat.id} value={cat.category_name} onSelect={() => { setEditFilterCategory(cat.id); setEditFilterSubCategory('all'); }}>
                                <Check className={`mr-2 h-3.5 w-3.5 ${editFilterCategory === cat.id ? 'opacity-100' : 'opacity-0'}`} />
                                {cat.category_name}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>

                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="h-8 justify-between rounded-lg border-gray-300 font-normal text-xs min-w-[120px]" disabled={editFilterCategory === 'all'} data-testid="edit-filter-subcategory">
                        <span className="truncate">{editFilterSubCategory !== 'all' ? editFilterSubcategories.find(c => c.id === editFilterSubCategory)?.category_name || 'Subcategory' : 'Subcategory'}</span>
                        <ChevronDown className="ml-1 h-3 w-3 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[200px] p-0" align="start">
                      <Command>
                        <CommandInput placeholder="Search..." />
                        <CommandList className="max-h-[200px]">
                          <CommandEmpty>No results found</CommandEmpty>
                          <CommandGroup>
                            <CommandItem onSelect={() => setEditFilterSubCategory('all')}>
                              <Check className={`mr-2 h-3.5 w-3.5 ${editFilterSubCategory === 'all' ? 'opacity-100' : 'opacity-0'}`} />
                              All Subcategories
                            </CommandItem>
                            {editFilterSubcategories.map(cat => (
                              <CommandItem key={cat.id} value={cat.category_name} onSelect={() => setEditFilterSubCategory(cat.id)}>
                                <Check className={`mr-2 h-3.5 w-3.5 ${editFilterSubCategory === cat.id ? 'opacity-100' : 'opacity-0'}`} />
                                {cat.category_name}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>

                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="h-8 justify-between rounded-lg border-gray-300 font-normal text-xs min-w-[120px]" data-testid="edit-filter-brand">
                        <span className="truncate">{editFilterBrand !== 'all' ? brands.find(b => b.id === editFilterBrand)?.brand_name || 'Brand' : 'Brand'}</span>
                        <ChevronDown className="ml-1 h-3 w-3 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[200px] p-0" align="start">
                      <Command>
                        <CommandInput placeholder="Search..." />
                        <CommandList className="max-h-[200px]">
                          <CommandEmpty>No results found</CommandEmpty>
                          <CommandGroup>
                            <CommandItem onSelect={() => setEditFilterBrand('all')}>
                              <Check className={`mr-2 h-3.5 w-3.5 ${editFilterBrand === 'all' ? 'opacity-100' : 'opacity-0'}`} />
                              All Brands
                            </CommandItem>
                            {brands.map(brand => (
                              <CommandItem key={brand.id} value={brand.brand_name} onSelect={() => setEditFilterBrand(brand.id)}>
                                <Check className={`mr-2 h-3.5 w-3.5 ${editFilterBrand === brand.id ? 'opacity-100' : 'opacity-0'}`} />
                                {brand.brand_name}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>

                  {(editFilterCategory !== 'all' || editFilterBrand !== 'all') && (
                    <Button variant="ghost" size="sm" className="h-8 text-xs text-[#6B7280] hover:text-red-500" onClick={() => { setEditFilterCategory('all'); setEditFilterSubCategory('all'); setEditFilterBrand('all'); }}>
                      Clear
                    </Button>
                  )}
                </div>

                {/* Item search row */}
                <div className="relative">
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#6B7280]" size={16} />
                    <Input
                      placeholder="Search items..."
                      value={editItemSearch}
                      onChange={(e) => { setEditItemSearch(e.target.value); setShowEditItemDropdown(true); }}
                      onFocus={() => setShowEditItemDropdown(true)}
                      className="h-10 pl-8 rounded-lg border-gray-300"
                      data-testid="edit-add-item-search"
                    />
                  </div>
                  {showEditItemDropdown && editItemSearch.trim() && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      {filteredEditItems.length === 0 ? (
                        <p className="px-3 py-2 text-sm text-[#6B7280]">No items found</p>
                      ) : filteredEditItems.map(item => (
                        <button
                          key={item.id}
                          type="button"
                          className="w-full px-3 py-2 text-left hover:bg-[#E6F2FA] transition-colors border-b border-gray-50 last:border-0"
                          onClick={() => {
                            handleEditItemSelect(item.id);
                            setEditItemSearch(`${item.item_name} (${item.internal_code})`);
                            setShowEditItemDropdown(false);
                          }}
                          data-testid={`edit-search-item-${item.id}`}
                        >
                          <div className="flex items-start gap-2.5">
                            <ItemThumbnail imageIds={item.image_ids} size={36} alt={item.item_name} />
                            <div className="flex-1 min-w-0 flex justify-between items-start gap-2">
                              <div className="min-w-0 flex-1">
                                <p className="font-medium text-[#1F2937] text-sm">{item.item_name}</p>
                                <p className="text-xs text-[#6B7280]">
                                  {getBrandName(item.brand_id)}{getBrandName(item.brand_id) && getCategoryName(item.category_id) ? ' · ' : ''}{getCategoryName(item.category_id)}
                                </p>
                              </div>
                              <span className="font-mono text-xs text-[#0776BD] bg-[#E6F2FA] px-1.5 py-0.5 rounded shrink-0">{item.internal_code}</span>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Package selection (when vendor pricing has packages) */}
                {editItemPackages.length > 0 && (
                  <div className="grid grid-cols-4 gap-3">
                    <div className="col-span-2">
                      <Select value={editSelectedPackage?.id || ''} onValueChange={(val) => {
                        const pkg = editItemPackages.find(p => p.id === val);
                        setEditSelectedPackage(pkg);
                        setEditPackagesQty('');
                        setEditPricePerPackage(String(pkg?.package_price || pkg?.unit_price * (pkg?.units_per_package || 1) || ''));
                      }}>
                        <SelectTrigger className="h-10 rounded-lg border-gray-300" data-testid="edit-package-select">
                          <SelectValue placeholder="Select package" />
                        </SelectTrigger>
                        <SelectContent>
                          {editItemPackages.map(pkg => (
                            <SelectItem key={pkg.id} value={pkg.id}>
                              {pkg.package_label || 'Single'} ({pkg.units_per_package || 1} unit{(pkg.units_per_package || 1) > 1 ? 's' : ''}) — ${(pkg.package_price || pkg.unit_price * (pkg.units_per_package || 1)).toFixed(2)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <Input type="number" min="1" placeholder="Pkgs Qty" value={editPackagesQty} onChange={(e) => setEditPackagesQty(e.target.value)} className="h-10 rounded-lg border-gray-300" data-testid="edit-packages-qty" />
                    <div className="flex gap-2">
                      <Input type="number" min="0.01" step="0.01" placeholder="Pkg Price" value={editPricePerPackage} onChange={(e) => setEditPricePerPackage(e.target.value)} className="h-10 rounded-lg border-gray-300 flex-1" data-testid="edit-pkg-price" />
                      <Button type="button" onClick={() => { addEditLineItem(); setEditItemSearch(''); }} disabled={!editPackagesQty || !editPricePerPackage} className="h-10 px-3 bg-[#0776BD] hover:bg-[#055A92] rounded-lg" data-testid="edit-add-item-btn">
                        <Plus size={18} />
                      </Button>
                    </div>
                  </div>
                )}

                {/* Package calculation preview */}
                {editSelectedPackage && editPackagesQty && editPricePerPackage && (
                  <div className="p-2.5 bg-blue-50 border border-blue-200 rounded-lg text-sm" data-testid="edit-package-preview">
                    <div className="grid grid-cols-4 gap-2 text-center">
                      <div>
                        <span className="text-blue-600 text-xs">Package</span>
                        <p className="font-medium text-[#1F2937] text-sm">{editSelectedPackage.package_label || 'Single'}</p>
                      </div>
                      <div>
                        <span className="text-blue-600 text-xs">Total Units</span>
                        <p className="font-medium text-[#1F2937] text-sm">{parseInt(editPackagesQty) * (editSelectedPackage.units_per_package || 1)}</p>
                      </div>
                      <div>
                        <span className="text-blue-600 text-xs">Unit Cost</span>
                        <p className="font-medium text-[#1F2937] text-sm">${(parseFloat(editPricePerPackage) / (editSelectedPackage.units_per_package || 1)).toFixed(2)}</p>
                      </div>
                      <div>
                        <span className="text-blue-600 text-xs">Line Total</span>
                        <p className="font-medium text-[#1F2937] text-sm">${(parseInt(editPackagesQty) * parseFloat(editPricePerPackage)).toFixed(2)}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Fallback: simple qty/price when no packages defined */}
                {editItemPackages.length === 0 && (
                  <div className="grid grid-cols-4 gap-3">
                    <div className="col-span-2"></div>
                    <Input type="number" min="1" placeholder="Qty" value={newEditItem.quantity} onChange={(e) => setNewEditItem({ ...newEditItem, quantity: e.target.value })} className="h-10 rounded-lg border-gray-300" data-testid="edit-add-qty" />
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          placeholder={priceLookupLoading ? "Loading..." : "Price"}
                          value={newEditItem.unit_price}
                          readOnly
                          className="h-10 rounded-lg border-gray-300 bg-gray-50 cursor-not-allowed"
                          data-testid="edit-add-price"
                        />
                        {priceLookupLoading && <Loader2 size={14} className="absolute right-2 top-1/2 -translate-y-1/2 animate-spin text-[#6B7280]" />}
                      </div>
                      <Button type="button" onClick={() => { addEditLineItem(); setEditItemSearch(''); }} disabled={!newEditItem.unit_price} className="h-10 px-3 bg-[#0776BD] hover:bg-[#055A92] rounded-lg" data-testid="edit-add-item-btn-fallback">
                        <Plus size={18} />
                      </Button>
                    </div>
                  </div>
                )}

                {newEditItem.item_id && !newEditItem.unit_price && !priceLookupLoading && editItemPackages.length === 0 && (
                  <p className="text-xs text-amber-600 mt-1">No vendor price found for this item. Set a price in Item Vendor Pricing first.</p>
                )}
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            {!editMode && isAdmin() && (
              <Button onClick={() => openDeleteDialog(selectedInvoice)} variant="destructive" className="rounded-lg gap-2 mr-auto" data-testid="delete-invoice-btn">
                <Trash2 size={16} /> Delete Invoice
              </Button>
            )}
            {!editMode && isManager() && (
              <Button onClick={() => openEditMode(selectedInvoice)} disabled={editLoading} className="bg-amber-500 hover:bg-amber-600 text-white rounded-lg gap-2" data-testid="edit-invoice-btn">
                {editLoading ? <Loader2 size={16} className="animate-spin" /> : <Pencil size={16} />}
                Edit Invoice
              </Button>
            )}
            {editMode && (
              <>
                <Button variant="outline" onClick={cancelEdit} className="rounded-lg" data-testid="cancel-edit-btn">Cancel</Button>
                <Button onClick={handleSaveEdit} disabled={editSaving} className="bg-[#0776BD] hover:bg-[#055A92] text-white rounded-lg gap-2" data-testid="save-edit-btn">
                  {editSaving ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle size={16} />}
                  Save Changes
                </Button>
              </>
            )}
            {!editMode && <Button variant="outline" onClick={() => setDetailOpen(false)} className="rounded-lg">Close</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Audit Trail Dialog */}
      <Dialog open={auditOpen} onOpenChange={setAuditOpen}>
        <DialogContent className="max-w-2xl rounded-xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-[#1F2937] flex items-center gap-2">
              <History className="text-amber-600" />
              Invoice Edit History
            </DialogTitle>
          </DialogHeader>
          {auditLoading ? (
            <div className="flex items-center justify-center py-8"><Loader2 className="h-8 w-8 animate-spin text-[#0776BD]" /></div>
          ) : auditData.length === 0 ? (
            <div className="py-8 text-center text-[#6B7280]">
              <History size={40} className="mx-auto mb-3 opacity-40" />
              <p>No edit history for this invoice</p>
            </div>
          ) : (
            <div className="space-y-3">
              {auditData.map((entry, idx) => (
                <div key={entry.id || `audit-${idx}`} className="p-3 border border-gray-200 rounded-lg bg-[#FAFAFA]" data-testid={`audit-entry-${idx}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium text-[#1F2937] text-sm">{entry.item_name}</span>
                    <Badge className={entry.quantity_delta > 0 ? 'bg-green-100 text-green-700 border-0' : entry.quantity_delta < 0 ? 'bg-red-100 text-red-700 border-0' : 'bg-gray-100 text-gray-700 border-0'}>
                      {entry.quantity_delta > 0 ? '+' : ''}{entry.quantity_delta}
                    </Badge>
                  </div>
                  <p className="text-xs text-[#6B7280]">{entry.notes}</p>
                  <div className="flex items-center justify-between mt-2 text-xs text-[#9CA3AF]">
                    <span>By: {entry.performed_by_name}</span>
                    <span>{new Date(entry.timestamp).toLocaleString()}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAuditOpen(false)} className="rounded-lg">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteOpen} onOpenChange={(open) => { if (!open) setDeleteOpen(false); }}>
        <DialogContent className="max-w-md rounded-xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-red-600 flex items-center gap-2">
              <AlertTriangle size={22} /> Delete Invoice
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-[#6B7280]">
              This will permanently delete invoice <strong className="text-[#1F2937]">{deleteTarget?.invoice_number}</strong> and reverse all stock entries. This action <strong>cannot be undone</strong>.
            </p>
            <div className="space-y-2">
              <Label className="text-sm font-medium text-[#1F2937]">Enter your admin password to confirm</Label>
              <div className="relative">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]" />
                <Input
                  type="password"
                  value={deletePassword}
                  onChange={(e) => setDeletePassword(e.target.value)}
                  placeholder="Admin password"
                  autoComplete="new-password"
                  className="pl-10 h-12 rounded-lg border-gray-300"
                  data-testid="delete-password-input"
                  onKeyDown={(e) => { if (e.key === 'Enter' && deletePassword) handleDelete(); }}
                />
              </div>
              {deleteError && <p className="text-sm text-red-600">{deleteError}</p>}
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteOpen(false)} className="rounded-lg">Cancel</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleting || !deletePassword} className="rounded-lg gap-1" data-testid="confirm-delete-btn">
              {deleting ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
              Delete Invoice
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PurchaseHistory;
